import { formatDate as format, formatName } from '../lib/format';
import PageContent from '../components/ui/PageContent';
import PageHeader from '../components/ui/PageHeader';
import { TableEmpty, Table, TableHead, TableRow, TableHeaderCell, TableCell } from '../components/ui/Table';
import Tabs from '../components/ui/Tabs';
import Button from '../components/ui/Button';
import { useEffect, useMemo, useRef, useState } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import useTableSort from "../components/useTableSort";
import { getDropdownLabel, getDropdownValue, postWithAuth } from "../common";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Dropdown from "../components/Dropdown";
import MyCalendar from "../components/Calendar";
import Input from "../components/Input";
import ModalComponent from '../components/Modal';
import Section from '../components/Section';
import { CheckIcon, PlusIcon, TrashIcon } from '../components/NavIcons';
import LoadingPopup from '../components/LoadingPopup';
// One list panel (Tasks or Appointments). Declared at module scope so React keeps
// the same instance across parent renders and the Active/Completed tab state sticks.
const RecordPanel = ({
    title,
    rows,
    addLabel,
    dateLabel,
    dateFormat,
    today,
    loading,
    onAdd,
    onView,
    onComplete,
    onDelete,
}) => {

    const [statusTab, setStatusTab] = useState('active')

    const visible = useMemo(
        () => rows.filter(row => statusTab === 'completed' ? !!row.completed : !row.completed),
        [rows, statusTab]
    )
    const sort = useTableSort(visible, { key: 'dueDate', direction: 'ascending' })

    return <Section title={title} className='width-100 recordPanelCard' variant='card'>
        <div className='listToolbar'>
            <Tabs className='recordPanelTabs' role='tablist' aria-label={`${title} status`}>
                {[
                    { key: 'active', label: 'Active' },
                    { key: 'completed', label: 'Completed' },
                ].map(tab => (
                    <Tabs.Tab active={statusTab === tab.key}
                        key={tab.key}
                        type='button'
                        role='tab'
                        aria-selected={statusTab === tab.key}
                        className={statusTab === tab.key ? 'recordPanelTab recordPanelTabActive' : 'recordPanelTab'}
                        onClick={() => setStatusTab(tab.key)}
                    >
                        {tab.label}
                    </Tabs.Tab>
                ))}
            </Tabs>

            <Button variant="create" size="md" className='allocationNewButton' onClick={onAdd}>
                <PlusIcon />
                {addLabel}
            </Button>
        </div>

        <div className='recordPanelTableWrap'>
            <div className='recordPanelTableDivider' aria-hidden='true' />
            <Table className='recordPanelTable'>
                <colgroup>
                    <col className='recordPanelDateColumn' />
                    <col className='recordPanelClientColumn' />
                    <col className='recordPanelSiteColumn' />
                    <col className='recordPanelCommentsColumn' />
                    <col className='recordPanelActionColumn' />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableHeaderCell className='recordPanelDateCell' {...sort.getSortableHeaderProps('dueDate')}>{dateLabel}</TableHeaderCell>
                        <TableHeaderCell className='recordPanelClientCell' {...sort.getSortableHeaderProps('client')}>Client</TableHeaderCell>
                        <TableHeaderCell className='recordPanelSiteCell' {...sort.getSortableHeaderProps('site')}>Site</TableHeaderCell>
                        <TableHeaderCell className='recordPanelCommentsCell'>Comments</TableHeaderCell>
                        <TableHeaderCell className='recordPanelActionCell'>ACTION</TableHeaderCell>
                    </TableRow>
                </TableHead>
                <tbody>
                    {sort.sortedData.map(task => {
                        const isOverdue = !task.completed && new Date(task.dueDate) < today
                        return (
                            <TableRow key={task.id} className={isOverdue ? 'recordRowOverdue' : ''}>
                                <TableCell overdue={isOverdue} onClick={() => onView(task)} className='hover recordPanelDateCell'>
                                    <span className='allocationDateLink recordPanelDueDateLink'>
                                        {format(new Date(task.dueDate), dateFormat)}
                                    </span>
                                </TableCell>
                                <TableCell className='recordPanelClientCell'>{task.client}</TableCell>
                                <TableCell className='recordPanelSiteCell'>{task.site}</TableCell>
                                <TableCell className='recordPanelCommentsCell'>{task.comments}</TableCell>
                                <TableCell className='recordPanelActionCell'>
                                    <div className='recordActionStack'>
                                        {!task.completed && (
                                            <Button variant="secondary" size="icon" className='recordActionIcon recordActionComplete' disabled={loading} onClick={() => onComplete(task)} aria-label={`Complete ${title.toLowerCase()}`} title='Complete'>
                                                <CheckIcon />
                                            </Button>
                                        )}
                                        <Button data-action-tone="delete" variant="danger" size="icon"  className='recordActionIcon recordActionDelete' disabled={loading} onClick={() => onDelete(task)} aria-label={`Delete ${title.toLowerCase()}`} title='Delete'>
                                            <TrashIcon />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                    {sort.sortedData.length === 0 && (
                        <TableEmpty  colSpan={5}>{`No ${title.toLowerCase()} yet.`}</TableEmpty>
                    )}
                </tbody>
            </Table>
        </div>
    </Section>
}

const Tasks = () => {

    const [loading, setLoading] = useState(false)
    const [isAdding, setIsAdding] = useState(false)
    const [tasks, setTasks] = useState([])
    const [clients, setClients] = useState([])
    const [sites, setSites] = useState([])
    const [organisationUsers, setOrganisationUsers] = useState([])

    const [editId, setEditId] = useState('')
    const [chosenClient, setChosenClient] = useState('')
    const [chosenSite, setChosenSite] = useState('')
    const [date, setDate] = useState('')
    const [appointmentTime, setAppointmentTime] = useState('')
    const [location, setLocation] = useState('')
    const [assignedStaffIds, setAssignedStaffIds] = useState([])
    const [comments, setComments] = useState('')
    const [completed, setCompleted] = useState(false)
    const [entryType, setEntryType] = useState('task')
    const [confirmMessage, setConfirmMessage] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)

    const userInfo = useSelector((state) => state.user.user)
    const activeOrg = useSelector((state) => state.user.activeOrg)

    useEffect(() => {
        if (activeOrg) {
            fetchTasks()
            fetchClients()
            fetchSites()
            fetchOrganisationUsers()
        }
    }, [activeOrg])

    // The org whose data is on screen; a slow reply for an org the user has since
    // switched away from must not replace the current org's tasks.
    const currentOrgIdRef = useRef(null)
    currentOrgIdRef.current = activeOrg?.id || null

    const fetchTasks = async () => {

        try {
            const requestedOrgId = activeOrg.id
            const resp = await postWithAuth('fetchTasks', { orgId: requestedOrgId })
            if (currentOrgIdRef.current !== requestedOrgId) return

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setTasks(resp.data.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))) // Sort by dueDate ascending. Sooner tasks first
            }
        } catch (e) {
            toast.error(e.message)
        }
    }



    const fetchClients = async () => {

        try {

            // Archived clients are included so tasks linked to them still show and
            // keep their client when edited; they are not offered for new links.
            const requestedOrgId = activeOrg.id
            const resp = await postWithAuth('fetchClients', { listOnly: true, orgId: requestedOrgId, includeArchived: true })
            if (currentOrgIdRef.current !== requestedOrgId) return

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setClients(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchSites = async () => {

        try {

            const resp = await postWithAuth('fetchSites', { listOnly: true, orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setSites(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchOrganisationUsers = async () => {

        try {

            const resp = await postWithAuth('fetchOrgAdminUsers', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setOrganisationUsers(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const userId = userInfo?.id
    const clientNameById = useMemo(
        () => new Map(clients.map(client => [client.id, client.name || ''])),
        [clients]
    )
    const siteNameById = useMemo(
        () => new Map(sites.map(site => [site.id, site.name || ''])),
        [sites]
    )
    const clientOptions = useMemo(
        () => clients.map(client => ({ label: client.name, value: client.id })),
        [clients]
    )
    const siteOptions = useMemo(
        () => sites.map(site => ({ label: site.name, value: site.id })),
        [sites]
    )
    const isTaskForUser = (task) => {
        if (!userId) return false
        return !task.userId || task.userId === userId || (Array.isArray(task.assignedStaffIds) && task.assignedStaffIds.includes(userId))
    }
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const tasksWithType = useMemo(
        () => tasks.filter(isTaskForUser).map(task => ({ ...task, type: task.type || 'task' })),
        [tasks, userId]
    )
    const decoratedTasks = useMemo(
        () => tasksWithType
            .map(row => ({
                ...row,
                client: clientNameById.get(row.chosenClient) || getDropdownLabel(row.chosenClient, clientOptions),
                site: siteNameById.get(row.chosenSite) || getDropdownLabel(row.chosenSite, siteOptions),
            }))
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)),
        [tasksWithType, clientNameById, siteNameById, clientOptions, siteOptions]
    )
    const taskRows = useMemo(() => decoratedTasks.filter(row => row.type === 'task'), [decoratedTasks])
    const appointmentRows = useMemo(() => decoratedTasks.filter(row => row.type === 'appointment'), [decoratedTasks])


    const handleDeleteTask = async (taskId) => {

        try {
            setLoading(true)

            const payload = {
                id: taskId,
                orgId: activeOrg.id,
            }

            const resp = await postWithAuth('deleteTask', payload)

            setIsAdding(false)

            setSuccessMessage({
                title: 'Item deleted',
                message: 'The task or appointment was deleted successfully.',
            })
            setLoading(false)
            fetchTasks()

        } catch (e) {
            toast.error(e.message)
            setLoading(false)
        }
    }

    const handleCompleteTask = async (taskId) => {
        try {
            setLoading(true)
            const resp = await postWithAuth('completeTask', { id: taskId, orgId: activeOrg.id })
            if (resp && resp.err) {
                toast.error(resp.err)
                setLoading(false)
                return
            }
            setSuccessMessage({
                title: 'Item completed',
                message: 'The task or appointment was completed successfully.',
            })
            setLoading(false)
            fetchTasks()
        } catch (e) {
            toast.error(e.message)
            setLoading(false)
        }
    }
    const handleSaveTask = async () => {


        try {
            setLoading(true)

            const taskType = entryType || 'task'
            const resolvedDueDate = new Date(date)
            if (taskType === 'appointment' && appointmentTime) {
                resolvedDueDate.setHours(
                    appointmentTime.getHours(),
                    appointmentTime.getMinutes(),
                    0,
                    0
                )
            }
            const payload = {
                id: editId,
                chosenClient: chosenClient ? chosenClient.value : '',
                chosenSite: chosenSite ? chosenSite.value : '',
                dueDate: resolvedDueDate.toISOString(),
                location: taskType === 'appointment' ? location.trim() : '',
                assignedStaffIds,
                comments,
                completed,
                type: taskType,
                orgId: activeOrg.id,
            }

            const resp = await postWithAuth('saveTask', payload)

            setIsAdding(false)

            setSuccessMessage({
                title: taskType === 'appointment' ? 'Appointment saved' : 'Task saved',
                message: `${taskType === 'appointment' ? 'Appointment' : 'Task'} information saved successfully.`,
            })
            setLoading(false)
            fetchTasks()

        } catch (e) {
            toast.error(e.message)
            setLoading(false)
        }
    }


    const handleView = (task) => {

        // Keep the stored link even when it is not in the lists (e.g. an archived
        // client or site), so saving the task does not silently remove it.
        setChosenClient(getDropdownValue(task.chosenClient, clients.map(r => ({ label: r.name, value: r.id })))
            || (task.chosenClient ? { label: clientNameById.get(task.chosenClient) || 'Unavailable client', value: task.chosenClient } : ''))
        setChosenSite(getDropdownValue(task.chosenSite, sites.map(r => ({ label: r.name, value: r.id })))
            || (task.chosenSite ? { label: siteNameById.get(task.chosenSite) || 'Unavailable site', value: task.chosenSite } : ''))
        const taskDate = new Date(task.dueDate)
        setDate(taskDate)
        setAppointmentTime((task.type || 'task') === 'appointment' ? taskDate : '')
        setLocation(task.location || '')
        setAssignedStaffIds(Array.isArray(task.assignedStaffIds) ? task.assignedStaffIds : [])
        setComments(task.comments || '')
        setCompleted(!!task.completed)
        setEntryType(task.type || 'task')
        setIsAdding(true)
        setEditId(task.id)
    }

    const clearFields = (kind) => {
        setChosenClient('')
        setChosenSite('')
        setDate('')
        setAppointmentTime('')
        setLocation('')
        setAssignedStaffIds([])
        setComments('')
        setCompleted(false)
        setEntryType(kind || 'task')
    }

    // Each panel's Add button opens the same dialog, pre-set to that panel's type.
    const openAdd = (kind) => {
        setEditId('')
        clearFields(kind)
        setIsAdding(true)
    }

    return <div>
        <Header />
        <LoadingPopup visible={loading} message="Loading..." />
        <div className='pageShell'>
            <Sidebar />
            <PageContent as="div" className='pageContent pageContentPadded tasksPageContent'>
                <PageHeader title={<>Tasks &amp; Appointments</>} subtitle={<>Track outstanding operations work and follow-ups</>} />


                <div className='recordPanelGrid'>
                    <RecordPanel
                        title={'Tasks'}
                        rows={taskRows}
                        addLabel={'Task'}
                        dateLabel={'Due Date'}
                        dateFormat={'EEE, d MMM yyyy'}
                        today={today}
                        loading={loading}
                        onAdd={() => openAdd('task')}
                        onView={handleView}
                        onComplete={(task) => setConfirmMessage({ message: 'Mark this task as complete?', action: () => handleCompleteTask(task.id), type: 'complete' })}
                        onDelete={(task) => setConfirmMessage({ message: 'Delete this task?', action: () => handleDeleteTask(task.id) })}
                    />

                    <RecordPanel
                        title={'Appointments'}
                        rows={appointmentRows}
                        addLabel={'Appointment'}
                        dateLabel={'Date/Time'}
                        dateFormat={'EEE, d MMM yyyy h:mm a'}
                        today={today}
                        loading={loading}
                        onAdd={() => openAdd('appointment')}
                        onView={handleView}
                        onComplete={(task) => setConfirmMessage({ message: 'Mark this appointment as complete?', action: () => handleCompleteTask(task.id), type: 'complete' })}
                        onDelete={(task) => setConfirmMessage({ message: 'Delete this appointment?', action: () => handleDeleteTask(task.id) })}
                    />
                </div>

                <ModalComponent
                    showModal={isAdding}
                    setShowModal={setIsAdding}
                    customMaxWidth='460px'
                    customContentStyle={{ minWidth: 0, width: 'min(460px, calc(100vw - 24px))', maxHeight: 'calc(100vh - 24px)', padding: 0, overflowY: 'auto' }}
                >
                    <div className='recordDialogShell'>
                        <div className='recordDialogHeader'>
                            <h2 className='modalTitle'>{editId ? (entryType === 'appointment' ? 'Edit Appointment' : 'Edit Task') : (entryType === 'appointment' ? 'Add Appointment' : 'Add Task')}</h2>
                        </div>
                        <div className='recordDialogBody'>
                            <MyCalendar
                                showDateOnly
                                noMaxDate
                                disabled={false}
                                startDate={date}
                                setStartDate={setDate}
                                label='Due Date'
                                placeholder='Select date'
                            />

                            {entryType === 'appointment' && (
                                <>
                                    <MyCalendar
                                        showTimeOnly
                                        noMaxDate
                                        disabled={false}
                                        startDate={appointmentTime}
                                        setStartDate={setAppointmentTime}
                                        label='Time'
                                        placeholder='Select time'
                                        timeIntervals={15}
                                        dateFormat='h:mm a'
                                    />
                                    <Input
                                        disabled={false}
                                        value={location}
                                        setValue={setLocation}
                                        placeholder='e.g. Client office, Level 3, 22 King St'
                                        label='Location'
                                    />
                                </>
                            )}

                            <Dropdown
                                clearable
                                options={[
                                    ...clients
                                        .filter(r => !r.archived || r.id === chosenClient?.value)
                                        .map(r => ({ label: r.archived ? `${r.name} (archived)` : r.name, value: r.id })),
                                    ...(chosenClient?.value && !clients.some(r => r.id === chosenClient.value) ? [chosenClient] : []),
                                ]}
                                value={chosenClient}
                                setValue={(value) => { setChosenClient(value); setChosenSite('') }}
                                placeholder='No client'
                                label='Client'
                                isSearchable={false}
                            />
                            <Dropdown
                                clearable
                                options={[
                                    ...sites.filter(r => chosenClient && r.clientId === chosenClient.value).map(r => ({ label: r.name, value: r.id })),
                                    ...(chosenSite?.value && !sites.some(r => r.id === chosenSite.value) ? [chosenSite] : []),
                                ]}
                                value={chosenSite}
                                setValue={setChosenSite}
                                placeholder='No site'
                                label='Site'
                                isSearchable={false}
                            />

                            <div className='recordStaffSection'>
                                <div className='recordStaffTitle'>Also assign to staff</div>
                                <div className='recordStaffDescription'>
                                    Selected staff receive the same {entryType === 'appointment' ? 'appointment' : 'task'}.
                                </div>
                                <div className='recordStaffChips'>
                                    {organisationUsers.map(user => {
                                        const userName = formatName(`${formatName(user.firstName) || ''} ${formatName(user.lastName) || ''}`.trim() || user.email || 'Unnamed user')
                                        const isSelected = assignedStaffIds.includes(user.id)
                                        return (
                                            <Button variant="secondary" size="md"
                                                type='button'
                                                key={user.id}
                                                className={isSelected ? 'recordStaffChip recordStaffChipSelected' : 'recordStaffChip'}
                                                aria-pressed={isSelected}
                                                onClick={() => setAssignedStaffIds(current => isSelected
                                                    ? current.filter(id => id !== user.id)
                                                    : [...current, user.id])}
                                            >
                                                <span aria-hidden='true'>{isSelected ? '✓' : '+'}</span>
                                                {formatName(userName)}
                                            </Button>
                                        )
                                    })}
                                    {organisationUsers.length === 0 && <span className='recordStaffEmpty'>No organisation users available.</span>}
                                </div>
                            </div>

                            <div className='recordDialogComments'>
                                <Input multiline disabled={false} value={comments} setValue={setComments} label='Comments' />
                            </div>
                        </div>

                        <div className='recordDialogFooter'>
                            <div>
                                {editId && <Button data-action-tone="delete" variant="danger" size="md"  className='allocationDangerButton' disabled={loading} onClick={() => setConfirmMessage({ message: entryType === 'appointment' ? 'Delete this appointment?' : 'Delete this task?', action: () => handleDeleteTask(editId) })}>Delete</Button>}
                            </div>
                            <div className='recordDialogFooterActions'>
                                <Button variant="secondary" size="md" className='secondaryButton tasksInlineActionButton' onClick={() => { setIsAdding(false) }}>Cancel</Button>
                                <Button data-action-tone="save" variant={editId ? 'soft' : 'create'} size="md"
                                    className={`tasksInlineActionButton recordDialogSaveButton ${editId ? 'saveWithIcon' : ''}`}
                                    disabled={loading || !date || (entryType === 'appointment' && !appointmentTime) || !comments}
                                    onClick={handleSaveTask}
                                >
                                    {!editId && <span aria-hidden='true'>+</span>}
                                    {editId ? 'Save' : `Add ${entryType === 'appointment' ? 'Appointment' : 'Task'}`}
                                </Button>
                            </div>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!successMessage}
                    setShowModal={setSuccessMessage}
                    customMaxWidth='300px'
                    customContentStyle={{ minWidth: 0, width: 'min(300px, calc(100vw - 32px))', padding: 0, overflow: 'hidden' }}
                >
                    <div className='timesheetSuccessPage recordSuccessPage'>
                        <div className='timesheetSuccessContent'>
                            <div className='timesheetSuccessIcon' aria-hidden='true'>
                                <svg viewBox='0 0 24 24'>
                                    <path d='M5 12.5l4.2 4.2L19 7' />
                                </svg>
                            </div>
                            <h2>{successMessage?.title}</h2>
                            <p>{successMessage?.message}</p>
                        </div>
                        <div className='timesheetSuccessFooter'>
                            <Button variant="secondary" size="md" onClick={() => setSuccessMessage(null)}>Cancel</Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!confirmMessage}
                    setShowModal={setConfirmMessage}
                    customMaxWidth='300px'
                    customContentStyle={{ minWidth: 0, width: 'min(300px, calc(100vw - 32px))', padding: '14px' }}
                >
                    <div className='allocationConfirmMessage recordConfirmMessage'>
                        <h2 className='modalTitle'>Are you sure?</h2>
                        <p className='modalSubtitle'>{confirmMessage?.message}</p>
                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => setConfirmMessage(null)}>Cancel</Button>
                            <Button variant={confirmMessage?.type === 'complete' ? 'secondary' : 'danger'} size="md" className={confirmMessage?.type === 'complete' ? 'taskConfirmCompleteButton' : 'modalDangerButton'} disabled={loading} onClick={async () => { await confirmMessage?.action?.(); setConfirmMessage(null) }}>Yes</Button>
                        </div>
                    </div>
                </ModalComponent>
            </PageContent>
        </div>
    </div >
}

export default Tasks
